import React, { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
    Box, Typography, Button, Table, TableBody,
    TableCell, TableHead, TableRow, IconButton,
    Dialog, DialogTitle, DialogContent, DialogActions, TextField,
    Grid, MenuItem, Select, FormControl, InputLabel, Alert,
    LinearProgress, Skeleton, Collapse, Stack, Divider,
    Accordion, AccordionSummary, AccordionDetails,
    Checkbox, FormControlLabel, Chip,
} from '@mui/material';
import {
    Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
    Visibility as ViewIcon, CloudUpload as UploadIcon,
    Inventory as InventoryIcon, ShoppingBag as OrdersIcon,
    ExpandMore, ExpandLess, TrendingUp as RevenueIcon,
    CheckCircle as CheckIcon,
    Storefront as StorefrontIcon, PendingActions as PendingIcon,
    Person as PersonIcon, Email as EmailIcon, Phone as PhoneIcon,
    LocationOn as LocationIcon, AddCircleOutline as AddRowIcon,
    RemoveCircleOutline as RemoveRowIcon,
} from '@mui/icons-material';
import marketplaceService from '../services/marketplaceService';
import MpnLookup from '../components/MpnLookup';

// ─── Design tokens ────────────────────────────────────────────────────────────
const C = {
    blue:      '#1976d2',
    blueLight: '#e3f0fc',
    blueDark:  '#1256a0',
    bg:        '#f5f7fa',
    white:     '#ffffff',
    border:    '#e0e4ea',
    text:      '#1a1d23',
    muted:     '#6b7280',
    radius:    '6px',
};

// ─── Constants ────────────────────────────────────────────────────────────────
const defaultForm = {
    // Part Identity
    mpn: '', manufacturer: '', brand: '', series: '',
    other_part_numbers: '',           // comma-separated string → split on submit

    // Category & Description
    category: '', category_path: '',
    description: '', detailed_description: '',

    // Package / Physical
    package: '', package_type: '', mounting_type: '',
    manufacturer_package_code: '', size_dimension: '',
    height: '', pitch: '', pin_count: '',

    // Pricing & Inventory
    price: '', currency: 'USD',
    listed_quantity: '', available_quantity: '',
    minimum_order_quantity: 1, standard_pack_qty: '',
    max_order_quantity: '', lead_time_weeks: '',
    back_order_allowed: false, ncnr: false,
    condition: 'new', location: '', bin_location: '', notes: '',

    // Lifecycle
    lifecycle_status: '', end_of_life: false, date_last_buy_chance: '',

    // Compliance
    rohs_status: '', reach_status: '', moisture_sensitivity_level: '',
    export_control_class: '', htsus_code: '',
    aec_qualified: false, halogen_free: false, lead_free: false,

    // Media
    datasheet_url: '', photo_url: '',
};

const LISTING_STYLE = {
    active:   { label: 'Active',   bg: '#e8f5e9', color: '#2e7d32' },
    sold_out: { label: 'Sold Out', bg: '#fdecea', color: '#c62828' },
    draft:    { label: 'Draft',    bg: '#e3f0fc', color: '#1565c0' },
    inactive: { label: 'Inactive', bg: '#f3f4f6', color: '#6b7280' },
};

const ORDER_STYLE = {
    pending:   { label: 'Pending',   bg: '#fff8e1', color: '#f57f17' },
    confirmed: { label: 'Confirmed', bg: '#e3f0fc', color: '#1565c0' },
    shipped:   { label: 'Shipped',   bg: '#f3e5f5', color: '#7b1fa2' },
    delivered: { label: 'Delivered', bg: '#e8f5e9', color: '#2e7d32' },
    cancelled: { label: 'Cancelled', bg: '#fdecea', color: '#c62828' },
};

const NEXT_STATUSES = {
    pending:   ['confirmed', 'cancelled'],
    confirmed: ['shipped',   'cancelled'],
    shipped:   ['delivered'],
    delivered: [], cancelled: [],
};

// ─── Status pill ──────────────────────────────────────────────────────────────
const Pill = ({ status, map }) => {
    const s = map[status] || { label: status, bg: '#f3f4f6', color: '#6b7280' };
    return (
        <Box component="span" sx={{
            display: 'inline-block', px: 1.5, py: '3px', borderRadius: '20px',
            fontSize: 12, fontWeight: 600, bgcolor: s.bg, color: s.color,
            whiteSpace: 'nowrap', letterSpacing: '0.01em',
        }}>
            {s.label}
        </Box>
    );
};

// ─── Stat card ────────────────────────────────────────────────────────────────
const StatCard = ({ icon, label, value, iconColor = C.blue, iconBg = C.blueLight }) => (
    <Box sx={{
        flex: 1, p: 2.5, bgcolor: C.white, border: `1px solid ${C.border}`,
        borderRadius: C.radius,
    }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Box>
                <Typography sx={{ fontSize: 12, color: C.muted, mb: 0.4, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>
                    {label}
                </Typography>
                <Typography sx={{ fontSize: 24, fontWeight: 700, color: C.text }}>{value}</Typography>
            </Box>
            <Box sx={{ bgcolor: iconBg, borderRadius: '8px', p: 1.2, display: 'flex' }}>
                {React.cloneElement(icon, { sx: { fontSize: 22, color: iconColor } })}
            </Box>
        </Stack>
    </Box>
);

// ─── Order panel (expanded) ───────────────────────────────────────────────────
const OrderPanel = ({ orderId }) => {
    const queryClient = useQueryClient();
    const [newStatus, setNewStatus] = useState('');
    const [trackingNumber, setTrackingNumber] = useState('');
    const [statusError, setStatusError] = useState(null);

    const { data: order, isLoading, isError, error } = useQuery({
        queryKey: ['order', orderId],
        queryFn: () => marketplaceService.getOrder(orderId),
        staleTime: 60 * 1000,
        retry: (failCount, err) => {
            const s = err?.response?.status;
            if (s === 404 || s === 403 || s === 429) return false;
            return failCount < 2;
        },
    });

    const componentIds = order?.items?.map((i) => i.component_id).filter(Boolean) || [];
    const { data: componentMap = {} } = useQuery({
        queryKey: ['orderComponents', componentIds],
        queryFn: async () => {
            const results = await Promise.all(
                componentIds.map((id) => marketplaceService.getComponent(id).catch(() => null))
            );
            return Object.fromEntries(results.filter(Boolean).map((c) => [c.id, c]));
        },
        enabled: componentIds.length > 0,
        staleTime: 5 * 60 * 1000,
    });

    const { data: imageMap = {} } = useQuery({
        queryKey: ['orderComponentImages', componentIds],
        queryFn: async () => {
            const entries = await Promise.all(
                componentIds.map(async (id) => {
                    try {
                        const imgs = await marketplaceService.getComponentImages(id);
                        const primary = imgs?.find(i => i.is_primary) || imgs?.[0];
                        return [id, primary?.image_url || null];
                    } catch { return [id, null]; }
                })
            );
            return Object.fromEntries(entries);
        },
        enabled: componentIds.length > 0,
        staleTime: 5 * 60 * 1000,
    });

    const updateMutation = useMutation({
        mutationFn: ({ status, tracking }) =>
            marketplaceService.updateOrderStatus(orderId, status, tracking || null),
        onSuccess: () => {
            queryClient.invalidateQueries(['order', orderId]);
            queryClient.invalidateQueries(['sellingOrders']);
            setNewStatus(''); setTrackingNumber(''); setStatusError(null);
        },
        onError: (err) => setStatusError(err.message),
    });

    if (isLoading) return (
        <Box sx={{ p: 3, bgcolor: C.bg }}>
            <Skeleton variant="rectangular" height={100} sx={{ borderRadius: C.radius }} />
        </Box>
    );

    if (isError) return (
        <Box sx={{ p: 3, bgcolor: C.bg }}>
            <Alert severity="error">{error?.message || 'Could not load order'}</Alert>
        </Box>
    );

    const nextStatuses = NEXT_STATUSES[order?.status] || [];
    const s = order?.shipping || {};
    const fullName = [s.first_name, s.last_name].filter(Boolean).join(' ');

    return (
        <Box sx={{ bgcolor: C.bg, borderTop: `1px solid ${C.border}`, px: 3, py: 3 }}>
            <Grid container spacing={3}>

                {/* Order Items */}
                <Grid item xs={12} md={5}>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1.5 }}>
                        Order Items
                    </Typography>
                    <Box sx={{ bgcolor: C.white, border: `1px solid ${C.border}`, borderRadius: C.radius, overflow: 'hidden' }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: C.bg }}>
                                    <TableCell sx={{ fontSize: 11, fontWeight: 600, color: C.muted, py: 1 }}>Component</TableCell>
                                    <TableCell align="center" sx={{ fontSize: 11, fontWeight: 600, color: C.muted, py: 1 }}>Qty</TableCell>
                                    <TableCell align="right" sx={{ fontSize: 11, fontWeight: 600, color: C.muted, py: 1 }}>Subtotal</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {order?.items?.map((item) => (
                                    <TableRow key={item.id}>
                                        <TableCell sx={{ fontSize: 13, color: C.text }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Box sx={{
                                                    width: 32, height: 32, borderRadius: '4px', flexShrink: 0,
                                                    bgcolor: C.blueLight, overflow: 'hidden',
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                }}>
                                                    {imageMap[item.component_id] ? (
                                                        <Box component="img"
                                                            src={imageMap[item.component_id]}
                                                            alt=""
                                                            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                            onError={(e) => { e.target.style.display = 'none'; }}
                                                        />
                                                    ) : (
                                                        <InventoryIcon sx={{ fontSize: 16, color: C.blue }} />
                                                    )}
                                                </Box>
                                                <Box>
                                                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: C.text }}>
                                                        {componentMap[item.component_id]?.mpn || item.mpn || item.part_number || `#${item.component_id}`}
                                                    </Typography>
                                                    {componentMap[item.component_id]?.manufacturer && (
                                                        <Typography sx={{ fontSize: 11, color: C.muted }}>
                                                            {componentMap[item.component_id].manufacturer}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Box>
                                        </TableCell>
                                        <TableCell align="center" sx={{ fontSize: 13 }}>{item.quantity}</TableCell>
                                        <TableCell align="right" sx={{ fontSize: 13, fontWeight: 600 }}>
                                            ${parseFloat(item.subtotal).toFixed(2)}
                                        </TableCell>
                                    </TableRow>
                                ))}
                                <TableRow sx={{ bgcolor: C.bg }}>
                                    <TableCell colSpan={2} sx={{ fontSize: 13, fontWeight: 700 }}>Order Total</TableCell>
                                    <TableCell align="right" sx={{ fontSize: 13, fontWeight: 700, color: C.blue }}>
                                        ${parseFloat(order?.total || 0).toFixed(2)}
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </Box>
                </Grid>

                {/* Shipping Details */}
                <Grid item xs={12} md={4}>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1.5 }}>
                        Shipping Address
                    </Typography>
                    <Box sx={{ bgcolor: C.white, border: `1px solid ${C.border}`, borderRadius: C.radius, p: 2 }}>
                        {fullName && (
                            <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 1 }}>
                                <PersonIcon sx={{ fontSize: 15, color: C.muted, mt: '2px' }} />
                                <Typography sx={{ fontSize: 13, color: C.text, fontWeight: 600 }}>{fullName}</Typography>
                            </Stack>
                        )}
                        {s.address && (
                            <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 1 }}>
                                <LocationIcon sx={{ fontSize: 15, color: C.muted, mt: '2px' }} />
                                <Box>
                                    <Typography sx={{ fontSize: 13, color: C.text }}>{s.address}</Typography>
                                    <Typography sx={{ fontSize: 13, color: C.text }}>
                                        {[s.city, s.state, s.zip_code].filter(Boolean).join(', ')}
                                    </Typography>
                                    {s.country && (
                                        <Typography sx={{ fontSize: 13, color: C.muted }}>{s.country}</Typography>
                                    )}
                                </Box>
                            </Stack>
                        )}
                        {s.email && (
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                                <EmailIcon sx={{ fontSize: 15, color: C.muted }} />
                                <Typography sx={{ fontSize: 13, color: C.text }}>{s.email}</Typography>
                            </Stack>
                        )}
                        {s.phone && (
                            <Stack direction="row" spacing={1} alignItems="center">
                                <PhoneIcon sx={{ fontSize: 15, color: C.muted }} />
                                <Typography sx={{ fontSize: 13, color: C.text }}>{s.phone}</Typography>
                            </Stack>
                        )}
                    </Box>
                </Grid>

                {/* Status & Update */}
                <Grid item xs={12} md={3}>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.06em', mb: 1.5 }}>
                        Fulfillment
                    </Typography>
                    <Box sx={{ bgcolor: C.white, border: `1px solid ${C.border}`, borderRadius: C.radius, p: 2 }}>
                        <Box sx={{ mb: 2 }}>
                            <Typography sx={{ fontSize: 11, color: C.muted, mb: 0.8 }}>Current status</Typography>
                            <Pill status={order?.status} map={ORDER_STYLE} />
                        </Box>

                        {nextStatuses.length > 0 && (
                            <Box>
                                <Typography sx={{ fontSize: 11, color: C.muted, mb: 0.8 }}>Update to</Typography>
                                <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                                    <Select
                                        displayEmpty value={newStatus}
                                        onChange={(e) => setNewStatus(e.target.value)}
                                        sx={{ fontSize: 13, borderRadius: C.radius }}
                                        renderValue={(v) => v
                                            ? (ORDER_STYLE[v]?.label || v)
                                            : <span style={{ color: C.muted }}>Select…</span>
                                        }
                                    >
                                        {nextStatuses.map((st) => (
                                            <MenuItem key={st} value={st} sx={{ fontSize: 13 }}>
                                                {ORDER_STYLE[st]?.label || st}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                {newStatus === 'shipped' && (
                                    <TextField
                                        fullWidth size="small" placeholder="Tracking # (optional)"
                                        value={trackingNumber}
                                        onChange={(e) => setTrackingNumber(e.target.value)}
                                        sx={{ mb: 1, '& .MuiOutlinedInput-root': { fontSize: 13, borderRadius: C.radius } }}
                                    />
                                )}

                                <Button fullWidth variant="contained" size="small"
                                    disabled={!newStatus || updateMutation.isPending}
                                    onClick={() => updateMutation.mutate({ status: newStatus, tracking: trackingNumber })}
                                    sx={{
                                        bgcolor: C.blue, borderRadius: C.radius, textTransform: 'none',
                                        fontWeight: 600, fontSize: 13, boxShadow: 'none',
                                        '&:hover': { bgcolor: C.blueDark, boxShadow: 'none' },
                                        '&:disabled': { bgcolor: C.border, color: C.muted },
                                    }}
                                >
                                    {updateMutation.isPending ? 'Updating…' : 'Update Status'}
                                </Button>
                            </Box>
                        )}

                        {statusError && (
                            <Alert severity="error" sx={{ mt: 1.5, fontSize: 12 }}>{statusError}</Alert>
                        )}
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
};

// ─── Sold Orders Tab ──────────────────────────────────────────────────────────
const SoldOrdersTab = () => {
    const [expandedId, setExpandedId] = useState(null);

    const { data: orders, isLoading, isError, refetch } = useQuery({
        queryKey: ['sellingOrders'],
        queryFn: () => marketplaceService.getSellingOrders(),
        staleTime: 60 * 1000,
    });

    if (isLoading) return (
        <Box>
            {[...Array(5)].map((_, i) => (
                <Box key={i} sx={{ px: 3, py: 2, borderBottom: `1px solid ${C.border}` }}>
                    <Skeleton variant="text" width="50%" height={20} />
                </Box>
            ))}
        </Box>
    );

    if (isError) return (
        <Box sx={{ p: 3 }}>
            <Alert severity="error" action={
                <Button size="small" onClick={refetch}>Retry</Button>
            }>
                Failed to load orders
            </Alert>
        </Box>
    );

    if (!orders?.length) return (
        <Box sx={{ py: 8, textAlign: 'center' }}>
            <OrdersIcon sx={{ fontSize: 48, color: C.muted, mb: 1.5 }} />
            <Typography sx={{ fontSize: 15, fontWeight: 600, color: C.text, mb: 0.5 }}>No orders yet</Typography>
            <Typography sx={{ fontSize: 13, color: C.muted }}>
                When buyers purchase your listings, orders will appear here.
            </Typography>
        </Box>
    );

    const totalRevenue = orders.reduce((s, o) => s + parseFloat(o.total || 0), 0);
    const pendingCount = orders.filter(o => o.status === 'pending').length;
    const shippedCount = orders.filter(o => ['shipped', 'delivered'].includes(o.status)).length;

    return (
        <Box>
            {/* Stats */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ p: 3, borderBottom: `1px solid ${C.border}` }}>
                <StatCard icon={<RevenueIcon />}  label="Total Revenue"      value={`$${totalRevenue.toFixed(2)}`} />
                <StatCard icon={<OrdersIcon />}   label="Total Orders"       value={orders.length} />
                <StatCard icon={<PendingIcon />}  label="Pending"            value={pendingCount}
                    iconColor="#f57f17" iconBg="#fff8e1" />
                <StatCard icon={<CheckIcon />}    label="Shipped / Delivered" value={shippedCount}
                    iconColor="#2e7d32" iconBg="#e8f5e9" />
            </Stack>

            {/* Table header */}
            <Box sx={{
                display: 'grid', gridTemplateColumns: '2fr 1.2fr 0.8fr 1fr 1.2fr 40px',
                px: 3, py: 1.5, bgcolor: C.bg, borderBottom: `1px solid ${C.border}`,
            }}>
                {['Order ID', 'Date', 'Items', 'Total', 'Status', ''].map((h, i) => (
                    <Typography key={i} sx={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                        {h}
                    </Typography>
                ))}
            </Box>

            {orders.map((order, idx) => {
                const isOpen = expandedId === order.order_id;
                return (
                    <React.Fragment key={order.order_id}>
                        <Box
                            onClick={() => setExpandedId(isOpen ? null : order.order_id)}
                            sx={{
                                display: 'grid', gridTemplateColumns: '2fr 1.2fr 0.8fr 1fr 1.2fr 40px',
                                px: 3, py: 2, alignItems: 'center', cursor: 'pointer',
                                borderBottom: `1px solid ${C.border}`,
                                bgcolor: isOpen ? C.bg : C.white,
                                '&:hover': { bgcolor: C.bg },
                                transition: 'background 0.12s',
                            }}
                        >
                            <Typography sx={{ fontSize: 13, fontWeight: 600, fontFamily: 'monospace', color: C.blue }}>
                                {order.order_id}
                            </Typography>
                            <Typography sx={{ fontSize: 13, color: C.muted }}>
                                {new Date(order.created_at).toLocaleDateString('en-US', {
                                    month: 'short', day: 'numeric', year: 'numeric',
                                })}
                            </Typography>
                            <Typography sx={{ fontSize: 13, color: C.text }}>
                                {order.items?.length ?? '—'} {order.items?.length === 1 ? 'item' : 'items'}
                            </Typography>
                            <Typography sx={{ fontSize: 13, fontWeight: 700, color: C.text }}>
                                ${parseFloat(order.total || 0).toFixed(2)}
                            </Typography>
                            <Pill status={order.status} map={ORDER_STYLE} />
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <IconButton size="small" sx={{ color: C.muted }}>
                                    {isOpen ? <ExpandLess sx={{ fontSize: 18 }} /> : <ExpandMore sx={{ fontSize: 18 }} />}
                                </IconButton>
                            </Box>
                        </Box>
                        <Collapse in={isOpen} unmountOnExit>
                            <OrderPanel orderId={order.order_id} />
                        </Collapse>
                    </React.Fragment>
                );
            })}
        </Box>
    );
};

// ─── Accordion section wrapper ────────────────────────────────────────────────
const FormSection = ({ title, badge, defaultExpanded = false, children }) => (
    <Accordion defaultExpanded={defaultExpanded} disableGutters elevation={0}
        sx={{
            border: `1px solid ${C.border}`, borderRadius: `${C.radius} !important`,
            mb: 1.5, '&:before': { display: 'none' },
            '&.Mui-expanded': { mb: 1.5 },
        }}>
        <AccordionSummary expandIcon={<ExpandMore sx={{ fontSize: 18 }} />}
            sx={{ minHeight: 44, px: 2, '& .MuiAccordionSummary-content': { my: 0 } }}>
            <Stack direction="row" alignItems="center" spacing={1}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: C.text }}>{title}</Typography>
                {badge && (
                    <Chip label={badge} size="small"
                        sx={{ height: 18, fontSize: 10, fontWeight: 600, bgcolor: C.blueLight, color: C.blue }} />
                )}
            </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 2, pt: 0, pb: 2 }}>
            {children}
        </AccordionDetails>
    </Accordion>
);

// ─── Listings Tab ─────────────────────────────────────────────────────────────
const ListingsTab = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const fileInputRef = useRef(null);

    const [openDialog, setOpenDialog] = useState(false);
    const [editingComponent, setEditingComponent] = useState(null);
    const [formData, setFormData] = useState(defaultForm);
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState('');
    const [uploading, setUploading] = useState(false);
    const [statusFilter, setStatusFilter] = useState('');


    // Dynamic rows: price breaks & technical parameters
    const [priceBreaks, setPriceBreaks] = useState([]);
    const [parameters, setParameters] = useState([]);

    const set = (field) => (e) => setFormData((f) => ({ ...f, [field]: e.target.value }));
    const setCheck = (field) => (e) => setFormData((f) => ({ ...f, [field]: e.target.checked }));

    const handleMpnResult = (fields, priceBreaks, parameters) => {
        setFormData((f) => ({
            ...f,
            mpn:                        fields.mpn                        || f.mpn,
            manufacturer:               fields.manufacturer               || f.manufacturer,
            brand:                      fields.brand                      || f.brand,
            series:                     fields.series                     || f.series,
            other_part_numbers:         fields.other_part_numbers         || f.other_part_numbers,
            category:                   fields.category                   || f.category,
            category_path:              fields.category_path              || f.category_path,
            description:                fields.description                || f.description,
            detailed_description:       fields.detailed_description       || f.detailed_description,
            package:                    fields.package                    || f.package,
            mounting_type:              fields.mounting_type              || f.mounting_type,
            minimum_order_quantity:     fields.minimum_order_quantity     ?? f.minimum_order_quantity,
            lead_time_weeks:            fields.lead_time_weeks            ?? f.lead_time_weeks,
            back_order_allowed:         fields.back_order_allowed         ?? f.back_order_allowed,
            ncnr:                       fields.ncnr                       ?? f.ncnr,
            lifecycle_status:           fields.lifecycle_status           || f.lifecycle_status,
            end_of_life:                fields.end_of_life                ?? f.end_of_life,
            datasheet_url:              fields.datasheet_url              || f.datasheet_url,
            photo_url:                  fields.photo_url                  || f.photo_url,
            rohs_status:                fields.rohs_status                || f.rohs_status,
            reach_status:               fields.reach_status               || f.reach_status,
            moisture_sensitivity_level: fields.moisture_sensitivity_level || f.moisture_sensitivity_level,
            export_control_class:       fields.export_control_class       || f.export_control_class,
            htsus_code:                 fields.htsus_code                 || f.htsus_code,
        }));
        if (priceBreaks.length) setPriceBreaks(priceBreaks);
        if (parameters.length)  setParameters(parameters);
    };

    const { data: components, isLoading, error } = useQuery({
        queryKey: ['myComponents', statusFilter],
        queryFn: () => marketplaceService.getMyComponents(statusFilter || null),
    });

    const componentIds = (components || []).map(c => c.id);
    const { data: imageMap = {} } = useQuery({
        queryKey: ['myComponentImages', componentIds],
        queryFn: async () => {
            const entries = await Promise.all(
                componentIds.map(async (id) => {
                    try {
                        const imgs = await marketplaceService.getComponentImages(id);
                        const primary = imgs?.find(i => i.is_primary) || imgs?.[0];
                        return [id, primary?.image_url || null];
                    } catch { return [id, null]; }
                })
            );
            return Object.fromEntries(entries);
        },
        enabled: componentIds.length > 0,
        staleTime: 5 * 60 * 1000,
    });

    const saveMutation = useMutation({
        mutationFn: async (data) => {
            if (editingComponent) return marketplaceService.updateComponent(editingComponent.id, data);
            const created = await marketplaceService.createComponent(data);
            if (imageFile) {
                try {
                    setUploading(true);
                    const uploaded = await marketplaceService.uploadImage(imageFile);
                    await marketplaceService.attachComponentImage(created.id, uploaded.url, true, 0);
                } catch (e) {
                    console.error('Image upload failed:', e.message);
                } finally {
                    setUploading(false);
                }
            }
            return created;
        },
        onSuccess: () => { queryClient.invalidateQueries(['myComponents']); handleCloseDialog(); },
    });

    const deleteMutation = useMutation({
        mutationFn: (id) => marketplaceService.deleteComponent(id),
        onSuccess: () => queryClient.invalidateQueries(['myComponents']),
    });

    const parseExtra = (raw) => {
        if (!raw) return {};
        try {
            let parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
            // Handle double-encoded: DB sometimes stores "\"{ ... }\"" so first parse gives a string
            if (typeof parsed === 'string') parsed = JSON.parse(parsed);
            return parsed && typeof parsed === 'object' ? parsed : {};
        } catch { return {}; }
    };

    const handleOpenDialog = (component = null) => {
        if (component) {
            setEditingComponent(component);

            const ex = parseExtra(component.extra);

            setFormData({
                // Core columns
                mpn:                    component.mpn             || '',
                manufacturer:           component.manufacturer    || '',
                description:            component.description     || '',
                package:                component.package         || '',
                category:               component.category        || '',
                datasheet_url:          component.datasheet_url   || '',
                listed_quantity:        component.listed_quantity  ?? '',
                available_quantity:     component.available_quantity ?? '',
                price:                  component.price           ?? '',
                currency:               component.currency        || 'USD',
                condition:              component.condition        || 'new',
                location:               component.location        || '',
                bin_location:           component.bin_location    || '',
                notes:                  component.notes           || '',
                minimum_order_quantity: component.minimum_order_quantity ?? 1,

                // Extended fields from `extra`
                brand:                      ex.brand                      || '',
                series:                     ex.series                     || '',
                other_part_numbers:         Array.isArray(ex.other_part_numbers)
                                                ? ex.other_part_numbers.join(', ')
                                                : (ex.other_part_numbers || ''),
                category_path:              ex.category_path              || '',
                detailed_description:       ex.detailed_description       || '',
                package_type:               ex.package_type               || '',
                mounting_type:              ex.mounting_type              || '',
                manufacturer_package_code:  ex.manufacturer_package_code  || '',
                size_dimension:             ex.size_dimension             || '',
                height:                     ex.height                     || '',
                pitch:                      ex.pitch                      || '',
                pin_count:                  ex.pin_count                  ?? '',
                standard_pack_qty:          ex.standard_pack_qty          ?? '',
                max_order_quantity:         ex.max_order_quantity         ?? '',
                lead_time_weeks:            ex.lead_time_weeks            ?? '',
                back_order_allowed:         ex.back_order_allowed         ?? false,
                ncnr:                       ex.ncnr                       ?? false,
                lifecycle_status:           ex.lifecycle_status           || '',
                end_of_life:                ex.end_of_life                ?? false,
                date_last_buy_chance:       ex.date_last_buy_chance       || '',
                photo_url:                  ex.photo_url                  || '',
                rohs_status:                ex.rohs_status                || '',
                reach_status:               ex.reach_status               || '',
                moisture_sensitivity_level: ex.moisture_sensitivity_level || '',
                export_control_class:       ex.export_control_class       || '',
                htsus_code:                 ex.htsus_code                 || '',
                aec_qualified:              ex.aec_qualified              ?? false,
                halogen_free:               ex.halogen_free               ?? false,
                lead_free:                  ex.lead_free                  ?? false,
            });

            setPriceBreaks(ex.price_breaks || []);
            setParameters(ex.parameters   || []);
            setImagePreview(ex.photo_url  || '');
            setImageFile(null);
        } else {
            setEditingComponent(null);
            setFormData(defaultForm);
            setPriceBreaks([]);
            setParameters([]);
            setImagePreview('');
            setImageFile(null);
        }
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false); setEditingComponent(null);
        setImageFile(null); setImagePreview('');
        setPriceBreaks([]); setParameters([]);
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setImageFile(file);
        const reader = new FileReader();
        reader.onloadend = () => setImagePreview(reader.result);
        reader.readAsDataURL(file);
    };

    const handleSubmit = (status = 'active') => {
        // All extended fields live in the `extra` text column (JSON string)
        const extraData = {};

        if (formData.brand)                      extraData.brand = formData.brand;
        if (formData.series)                     extraData.series = formData.series;
        if (formData.other_part_numbers)         extraData.other_part_numbers = formData.other_part_numbers.split(',').map(s => s.trim()).filter(Boolean);
        if (formData.category_path)              extraData.category_path = formData.category_path;
        if (formData.detailed_description)       extraData.detailed_description = formData.detailed_description;
        if (formData.package_type)               extraData.package_type = formData.package_type;
        if (formData.mounting_type)              extraData.mounting_type = formData.mounting_type;
        if (formData.manufacturer_package_code)  extraData.manufacturer_package_code = formData.manufacturer_package_code;
        if (formData.size_dimension)             extraData.size_dimension = formData.size_dimension;
        if (formData.height)                     extraData.height = formData.height;
        if (formData.pitch)                      extraData.pitch = formData.pitch;
        if (formData.pin_count)                  extraData.pin_count = parseInt(formData.pin_count);
        if (formData.standard_pack_qty)          extraData.standard_pack_qty = parseInt(formData.standard_pack_qty);
        if (formData.max_order_quantity)         extraData.max_order_quantity = parseInt(formData.max_order_quantity);
        if (formData.lead_time_weeks)            extraData.lead_time_weeks = parseInt(formData.lead_time_weeks);
        if (formData.back_order_allowed)         extraData.back_order_allowed = formData.back_order_allowed;
        if (formData.ncnr)                       extraData.ncnr = formData.ncnr;
        if (formData.photo_url)                  extraData.photo_url = formData.photo_url;
        if (formData.lifecycle_status)           extraData.lifecycle_status = formData.lifecycle_status;
        if (formData.end_of_life)                extraData.end_of_life = formData.end_of_life;
        if (formData.date_last_buy_chance)       extraData.date_last_buy_chance = formData.date_last_buy_chance;
        if (formData.rohs_status)                extraData.rohs_status = formData.rohs_status;
        if (formData.reach_status)               extraData.reach_status = formData.reach_status;
        if (formData.moisture_sensitivity_level) extraData.moisture_sensitivity_level = formData.moisture_sensitivity_level;
        if (formData.export_control_class)       extraData.export_control_class = formData.export_control_class;
        if (formData.htsus_code)                 extraData.htsus_code = formData.htsus_code;
        if (formData.aec_qualified)              extraData.aec_qualified = formData.aec_qualified;
        if (formData.halogen_free)               extraData.halogen_free = formData.halogen_free;
        if (formData.lead_free)                  extraData.lead_free = formData.lead_free;

        const validPriceBreaks = priceBreaks
            .filter(pb => pb.break_quantity && pb.unit_price)
            .map(pb => ({
                break_quantity: parseInt(pb.break_quantity),
                unit_price:     parseFloat(pb.unit_price),
                total_price:    parseInt(pb.break_quantity) * parseFloat(pb.unit_price),
            }));
        if (validPriceBreaks.length) extraData.price_breaks = validPriceBreaks;

        const validParams = parameters.filter(p => p.parameter_name && p.value);
        if (validParams.length) extraData.parameters = validParams;

        const data = {
            mpn:                    formData.mpn,
            manufacturer:           formData.manufacturer  || undefined,
            description:            formData.description,
            package:                formData.package       || undefined,
            category:               formData.category      || undefined,
            datasheet_url:          formData.datasheet_url || undefined,
            listed_quantity:        parseInt(formData.listed_quantity),
            available_quantity:     parseInt(formData.available_quantity),
            price:                  parseFloat(formData.price),
            currency:               formData.currency,
            condition:              formData.condition,
            status,
            location:               formData.location      || undefined,
            bin_location:           formData.bin_location  || undefined,
            notes:                  formData.notes         || undefined,
            minimum_order_quantity: parseInt(formData.minimum_order_quantity) || 1,
            extra:                  JSON.stringify(extraData),
        };

        saveMutation.mutate(data);
    };

    const handleDelete = (id) => {
        if (window.confirm('Permanently delete this listing?')) deleteMutation.mutate(id);
    };

    const handlePublish = (e, component) => {
        e.stopPropagation();
        marketplaceService.updateComponent(component.id, { status: 'active' })
            .then(() => queryClient.invalidateQueries(['myComponents']))
            .catch((err) => alert(err.message));
    };

    const filterOptions = [
        { label: 'All', value: '' },
        { label: 'Active', value: 'active' },
        { label: 'Draft', value: 'draft' },
        { label: 'Sold Out', value: 'sold_out' },
    ];

    const inputSx = { '& .MuiOutlinedInput-root': { borderRadius: C.radius, fontSize: 13 } };
    const selectSx = { borderRadius: C.radius, fontSize: 13 };

    return (
        <Box>
            {/* Toolbar */}
            <Stack direction="row" justifyContent="space-between" alignItems="center"
                sx={{ px: 3, py: 2, borderBottom: `1px solid ${C.border}` }}>
                <Stack direction="row" spacing={0.5}>
                    {filterOptions.map(({ label, value }) => (
                        <Box key={value} onClick={() => setStatusFilter(value)} sx={{
                            px: 2, py: 0.6, borderRadius: '20px', cursor: 'pointer',
                            fontSize: 13, fontWeight: statusFilter === value ? 600 : 400,
                            bgcolor: statusFilter === value ? C.blue : 'transparent',
                            color: statusFilter === value ? '#fff' : C.muted,
                            border: `1px solid ${statusFilter === value ? C.blue : C.border}`,
                            transition: 'all 0.15s',
                            '&:hover': { borderColor: C.blue, color: statusFilter === value ? '#fff' : C.blue },
                        }}>
                            {label}
                        </Box>
                    ))}
                </Stack>
                <Button variant="contained" startIcon={<AddIcon />}
                    onClick={() => handleOpenDialog()}
                    sx={{
                        bgcolor: C.blue, borderRadius: C.radius, textTransform: 'none',
                        fontWeight: 600, fontSize: 13, boxShadow: 'none',
                        '&:hover': { bgcolor: C.blueDark, boxShadow: 'none' },
                    }}>
                    Add listing
                </Button>
            </Stack>

            {isLoading && (
                <Box>
                    {[...Array(5)].map((_, i) => (
                        <Box key={i} sx={{ px: 3, py: 2.5, borderBottom: `1px solid ${C.border}` }}>
                            <Skeleton variant="text" width="45%" height={18} />
                        </Box>
                    ))}
                </Box>
            )}

            {error && <Alert severity="error" sx={{ m: 3 }}>{error.message}</Alert>}

            {!isLoading && components?.length === 0 && (
                <Box sx={{ py: 8, textAlign: 'center' }}>
                    <InventoryIcon sx={{ fontSize: 48, color: C.muted, mb: 1.5 }} />
                    <Typography sx={{ fontSize: 15, fontWeight: 600, color: C.text, mb: 0.5 }}>No listings yet</Typography>
                    <Typography sx={{ fontSize: 13, color: C.muted, mb: 3 }}>
                        Add your first product to start selling on PartzFlow.
                    </Typography>
                    <Button variant="contained" startIcon={<AddIcon />}
                        onClick={() => handleOpenDialog()}
                        sx={{
                            bgcolor: C.blue, borderRadius: C.radius, textTransform: 'none',
                            fontWeight: 600, boxShadow: 'none',
                            '&:hover': { bgcolor: C.blueDark, boxShadow: 'none' },
                        }}>
                        Add listing
                    </Button>
                </Box>
            )}

            {!isLoading && components?.length > 0 && (
                <>
                    {/* Table header */}
                    <Box sx={{
                        display: 'grid', gridTemplateColumns: '2.5fr 1.5fr 1fr 1fr 1fr 130px',
                        px: 3, py: 1.5, bgcolor: C.bg, borderBottom: `1px solid ${C.border}`,
                    }}>
                        {['Product', 'Manufacturer', 'Price', 'Stock', 'Status', ''].map((h, i) => (
                            <Typography key={i} sx={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                                {h}
                            </Typography>
                        ))}
                    </Box>

                    {components.map((c, idx) => (
                        <Box key={c.id} sx={{
                            display: 'grid', gridTemplateColumns: '2.5fr 1.5fr 1fr 1fr 1fr 130px',
                            px: 3, py: 2, alignItems: 'center',
                            borderBottom: idx < components.length - 1 ? `1px solid ${C.border}` : 'none',
                            bgcolor: C.white, '&:hover': { bgcolor: C.bg }, transition: 'background 0.12s',
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Box sx={{
                                    width: 40, height: 40, borderRadius: '6px', flexShrink: 0,
                                    bgcolor: C.blueLight, overflow: 'hidden',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    border: `1px solid ${C.border}`,
                                }}>
                                    {imageMap[c.id] ? (
                                        <Box component="img" src={imageMap[c.id]} alt={c.mpn}
                                            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                            onError={(e) => { e.target.style.display = 'none'; }}
                                        />
                                    ) : (
                                        <InventoryIcon sx={{ fontSize: 18, color: C.blue }} />
                                    )}
                                </Box>
                                <Box>
                                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: C.text }}>{c.mpn}</Typography>
                                    {c.category && (
                                        <Typography sx={{ fontSize: 11, color: C.muted }}>{c.category}</Typography>
                                    )}
                                </Box>
                            </Box>

                            <Typography sx={{ fontSize: 13, color: C.text }}>{c.manufacturer || '—'}</Typography>

                            <Typography sx={{ fontSize: 13, fontWeight: 600, color: C.text }}>
                                ${parseFloat(c.price).toFixed(2)}
                            </Typography>

                            <Box>
                                <Typography sx={{ fontSize: 13, fontWeight: 500, color: c.available_quantity > 0 ? C.text : '#c62828' }}>
                                    {c.available_quantity}
                                </Typography>
                                <Typography sx={{ fontSize: 11, color: C.muted }}>in stock</Typography>
                            </Box>

                            <Pill status={c.status || 'active'} map={LISTING_STYLE} />

                            <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                                {c.status === 'draft' && (
                                    <Box onClick={(e) => handlePublish(e, c)} sx={{
                                        px: 1.5, py: '3px', borderRadius: '20px', cursor: 'pointer',
                                        fontSize: 12, fontWeight: 600, bgcolor: C.blueLight, color: C.blue,
                                        border: `1px solid ${C.blue}40`,
                                        '&:hover': { bgcolor: C.blue, color: '#fff' },
                                        transition: 'all 0.15s',
                                    }}>
                                        Publish
                                    </Box>
                                )}
                                <IconButton size="small" sx={{ color: C.muted, '&:hover': { color: C.blue } }}
                                    onClick={() => navigate(`/parts/${c.id}/${encodeURIComponent(c.mpn || c.id)}`)}>
                                    <ViewIcon sx={{ fontSize: 17 }} />
                                </IconButton>
                                <IconButton size="small" sx={{ color: C.muted, '&:hover': { color: C.blue } }}
                                    onClick={() => handleOpenDialog(c)}>
                                    <EditIcon sx={{ fontSize: 17 }} />
                                </IconButton>
                                <IconButton size="small" sx={{ color: C.muted, '&:hover': { color: '#c62828' } }}
                                    onClick={() => handleDelete(c.id)}>
                                    <DeleteIcon sx={{ fontSize: 17 }} />
                                </IconButton>
                            </Stack>
                        </Box>
                    ))}
                </>
            )}

            {/* ── Dialog ─────────────────────────────────────────────────────── */}
            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="lg" fullWidth
                PaperProps={{ sx: { borderRadius: '10px', maxHeight: '92vh' } }}>
                <DialogTitle sx={{ fontSize: 16, fontWeight: 700, color: C.text, pb: 1.5 }}>
                    {editingComponent ? 'Edit listing' : 'New listing'}
                </DialogTitle>
                <Divider />

                <DialogContent sx={{ pt: 2 }}>

                    {/* ── MPN Lookup ───────────────────────────────────────── */}
                    {!editingComponent && (
                        <MpnLookup onResult={handleMpnResult} />
                    )}

                    {/* ── 1. Part Identity ─────────────────────────────────── */}
                    <FormSection title="Part Identity" defaultExpanded>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={4}>
                                <TextField fullWidth required label="MPN" value={formData.mpn}
                                    onChange={set('mpn')} size="small" sx={inputSx} />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField fullWidth label="Manufacturer" value={formData.manufacturer}
                                    onChange={set('manufacturer')} size="small" sx={inputSx} />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField fullWidth label="Brand" value={formData.brand}
                                    onChange={set('brand')} size="small" sx={inputSx}
                                    helperText="If different from manufacturer" />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField fullWidth label="Series / Family" value={formData.series}
                                    onChange={set('series')} size="small" sx={inputSx}
                                    placeholder="e.g. LM358, STM32F4" />
                            </Grid>
                            <Grid item xs={12} sm={8}>
                                <TextField fullWidth label="Alternate / Cross-Reference Part Numbers"
                                    value={formData.other_part_numbers}
                                    onChange={set('other_part_numbers')} size="small" sx={inputSx}
                                    helperText="Comma-separated, e.g. LM358AN, LM358N/NOPB" />
                            </Grid>
                        </Grid>
                    </FormSection>

                    {/* ── 2. Category & Description ────────────────────────── */}
                    <FormSection title="Category & Description" defaultExpanded>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={4}>
                                <TextField fullWidth label="Category" value={formData.category}
                                    onChange={set('category')} size="small" sx={inputSx}
                                    placeholder="e.g. Op Amps, Ceramic Capacitors" />
                            </Grid>
                            <Grid item xs={12} sm={8}>
                                <TextField fullWidth label="Category Path" value={formData.category_path}
                                    onChange={set('category_path')} size="small" sx={inputSx}
                                    placeholder="e.g. ICs > Linear > Op Amps" />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField fullWidth required label="Short Description" value={formData.description}
                                    onChange={set('description')} size="small" sx={inputSx}
                                    placeholder="e.g. IC OPAMP GP 2 CIRCUIT 8DIP" />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField fullWidth label="Detailed Description" value={formData.detailed_description}
                                    onChange={set('detailed_description')} size="small" sx={inputSx}
                                    multiline rows={3} />
                            </Grid>
                        </Grid>
                    </FormSection>

                    {/* ── 3. Package & Physical ─────────────────────────────── */}
                    <FormSection title="Package & Physical">
                        <Grid container spacing={2}>
                            {/* Row 1: package identity */}
                            <Grid item xs={12} sm={4}>
                                <TextField fullWidth label="Package / Case" value={formData.package}
                                    onChange={set('package')} size="small" sx={inputSx}
                                    placeholder="e.g. 8-PDIP, 0603, SOT-23" />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <FormControl fullWidth size="small">
                                    <InputLabel shrink>Pkg Type</InputLabel>
                                    <Select notched displayEmpty value={formData.package_type} label="Pkg Type"
                                        onChange={set('package_type')} sx={selectSx}>
                                        <MenuItem value="" sx={{ fontSize: 13 }}>— None —</MenuItem>
                                        {['bulk', 'tape_reel', 'cut_tape', 'tube', 'tray', 'strip', 'box', 'other'].map(v => (
                                            <MenuItem key={v} value={v} sx={{ fontSize: 13 }}>
                                                {v.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <FormControl fullWidth size="small">
                                    <InputLabel shrink>Mounting</InputLabel>
                                    <Select notched displayEmpty value={formData.mounting_type} label="Mounting"
                                        onChange={set('mounting_type')} sx={selectSx}>
                                        <MenuItem value="" sx={{ fontSize: 13 }}>— None —</MenuItem>
                                        {['Surface Mount', 'Through Hole', 'Through Hole / Surface Mount', 'Free-Standing', 'Chassis Mount', 'Panel Mount'].map(v => (
                                            <MenuItem key={v} value={v} sx={{ fontSize: 13 }}>{v}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            {/* Row 2: dimensions */}
                            <Grid item xs={12} sm={3}>
                                <TextField fullWidth label="Mfr Package Code" value={formData.manufacturer_package_code}
                                    onChange={set('manufacturer_package_code')} size="small" sx={inputSx}
                                    placeholder="e.g. PDIP-8, DFN8" />
                            </Grid>
                            <Grid item xs={12} sm={3}>
                                <TextField fullWidth label="Size / Dimension" value={formData.size_dimension}
                                    onChange={set('size_dimension')} size="small" sx={inputSx}
                                    placeholder="e.g. 1.60mm × 0.80mm" />
                            </Grid>
                            <Grid item xs={12} sm={2}>
                                <TextField fullWidth label="Height" value={formData.height}
                                    onChange={set('height')} size="small" sx={inputSx}
                                    placeholder="e.g. 0.90mm" />
                            </Grid>
                            <Grid item xs={12} sm={2}>
                                <TextField fullWidth label="Pitch" value={formData.pitch}
                                    onChange={set('pitch')} size="small" sx={inputSx}
                                    placeholder="e.g. 0.5mm" />
                            </Grid>
                            <Grid item xs={12} sm={2}>
                                <TextField fullWidth label="Pin Count" value={formData.pin_count}
                                    onChange={set('pin_count')} size="small" sx={inputSx}
                                    type="number" slotProps={{ htmlInput: { min: 1 } }} />
                            </Grid>
                        </Grid>
                    </FormSection>

                    {/* ── 4. Pricing & Inventory ───────────────────────────── */}
                    <FormSection title="Pricing & Inventory" defaultExpanded>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={3}>
                                <TextField fullWidth required label="Unit Price" value={formData.price}
                                    onChange={set('price')} size="small" sx={inputSx}
                                    type="number" inputProps={{ min: 0, step: 0.0001 }} />
                            </Grid>
                            <Grid item xs={12} sm={2}>
                                <FormControl fullWidth size="small">
                                    <InputLabel shrink>Currency</InputLabel>
                                    <Select notched value={formData.currency} label="Currency"
                                        onChange={set('currency')} sx={selectSx}>
                                        {['USD', 'EUR', 'GBP', 'CAD', 'AUD', 'JPY', 'INR', 'CNY'].map(v => (
                                            <MenuItem key={v} value={v} sx={{ fontSize: 13 }}>{v}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={2}>
                                <TextField fullWidth required label="Listed Qty" value={formData.listed_quantity}
                                    onChange={set('listed_quantity')} size="small" sx={inputSx}
                                    type="number" inputProps={{ min: 0 }} />
                            </Grid>
                            <Grid item xs={12} sm={2}>
                                <TextField fullWidth required label="Available Qty" value={formData.available_quantity}
                                    onChange={set('available_quantity')} size="small" sx={inputSx}
                                    type="number" inputProps={{ min: 0 }} />
                            </Grid>
                            <Grid item xs={12} sm={3}>
                                <FormControl fullWidth size="small">
                                    <InputLabel shrink>Condition</InputLabel>
                                    <Select notched value={formData.condition} label="Condition"
                                        onChange={set('condition')} sx={selectSx}>
                                        {['new', 'used', 'refurbished'].map(v => (
                                            <MenuItem key={v} value={v} sx={{ fontSize: 13 }}>
                                                {v.charAt(0).toUpperCase() + v.slice(1)}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={2}>
                                <TextField fullWidth label="MOQ" value={formData.minimum_order_quantity}
                                    onChange={set('minimum_order_quantity')} size="small" sx={inputSx}
                                    type="number" inputProps={{ min: 1 }}
                                    helperText="Min order qty" />
                            </Grid>
                            <Grid item xs={12} sm={2}>
                                <TextField fullWidth label="Std Pack Qty" value={formData.standard_pack_qty}
                                    onChange={set('standard_pack_qty')} size="small" sx={inputSx}
                                    type="number" inputProps={{ min: 1 }}
                                    helperText="Factory reel/tube qty" />
                            </Grid>
                            <Grid item xs={12} sm={2}>
                                <TextField fullWidth label="Max Order Qty" value={formData.max_order_quantity}
                                    onChange={set('max_order_quantity')} size="small" sx={inputSx}
                                    type="number" inputProps={{ min: 1 }} />
                            </Grid>
                            <Grid item xs={12} sm={2}>
                                <TextField fullWidth label="Lead Time (weeks)" value={formData.lead_time_weeks}
                                    onChange={set('lead_time_weeks')} size="small" sx={inputSx}
                                    type="number" inputProps={{ min: 0 }} />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <Stack direction="row" spacing={1}>
                                    <FormControlLabel
                                        control={<Checkbox checked={formData.back_order_allowed} onChange={setCheck('back_order_allowed')} size="small" />}
                                        label={<Typography sx={{ fontSize: 13 }}>Back Order OK</Typography>}
                                    />
                                    <FormControlLabel
                                        control={<Checkbox checked={formData.ncnr} onChange={setCheck('ncnr')} size="small" />}
                                        label={<Typography sx={{ fontSize: 13 }}>NCNR</Typography>}
                                    />
                                </Stack>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField fullWidth label="Warehouse Location" value={formData.location}
                                    onChange={set('location')} size="small" sx={inputSx}
                                    placeholder="e.g. US-West, Singapore" />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField fullWidth label="Bin / Shelf" value={formData.bin_location}
                                    onChange={set('bin_location')} size="small" sx={inputSx}
                                    placeholder="e.g. A3-B12" />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField fullWidth label="Internal Notes" value={formData.notes}
                                    onChange={set('notes')} size="small" sx={inputSx}
                                    multiline rows={2} />
                            </Grid>
                        </Grid>
                    </FormSection>

                    {/* ── 5. Volume Price Breaks ───────────────────────────── */}
                    <FormSection title="Volume Price Breaks" badge={priceBreaks.length ? `${priceBreaks.length} tiers` : null}>
                        <Box sx={{ mb: 1 }}>
                            {priceBreaks.length > 0 && (
                                <Grid container spacing={1} sx={{ mb: 1 }}>
                                    {['Min Qty', 'Unit Price', 'Total Price', ''].map((h, i) => (
                                        <Grid item xs={i === 3 ? 1 : 3} key={i}>
                                            <Typography sx={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                                {h}
                                            </Typography>
                                        </Grid>
                                    ))}
                                </Grid>
                            )}
                            {priceBreaks.map((pb, i) => (
                                <Grid container spacing={1} key={i} alignItems="center" sx={{ mb: 0.8 }}>
                                    <Grid item xs={3}>
                                        <TextField fullWidth size="small" type="number" placeholder="Qty"
                                            value={pb.break_quantity}
                                            inputProps={{ min: 1 }}
                                            onChange={(e) => {
                                                const rows = [...priceBreaks];
                                                rows[i] = { ...rows[i], break_quantity: e.target.value };
                                                setPriceBreaks(rows);
                                            }}
                                            sx={inputSx} />
                                    </Grid>
                                    <Grid item xs={3}>
                                        <TextField fullWidth size="small" type="number" placeholder="Unit price"
                                            value={pb.unit_price}
                                            inputProps={{ min: 0, step: 0.0001 }}
                                            onChange={(e) => {
                                                const rows = [...priceBreaks];
                                                rows[i] = { ...rows[i], unit_price: e.target.value };
                                                setPriceBreaks(rows);
                                            }}
                                            sx={inputSx} />
                                    </Grid>
                                    <Grid item xs={3}>
                                        <TextField fullWidth size="small" disabled
                                            value={pb.break_quantity && pb.unit_price
                                                ? `$${(parseFloat(pb.break_quantity) * parseFloat(pb.unit_price)).toFixed(4)}`
                                                : '—'}
                                            sx={inputSx} />
                                    </Grid>
                                    <Grid item xs={1}>
                                        <IconButton size="small" sx={{ color: '#c62828' }}
                                            onClick={() => setPriceBreaks(priceBreaks.filter((_, j) => j !== i))}>
                                            <RemoveRowIcon sx={{ fontSize: 18 }} />
                                        </IconButton>
                                    </Grid>
                                </Grid>
                            ))}
                        </Box>
                        <Button size="small" startIcon={<AddRowIcon sx={{ fontSize: 16 }} />}
                            onClick={() => setPriceBreaks([...priceBreaks, { break_quantity: '', unit_price: '' }])}
                            sx={{ textTransform: 'none', fontSize: 13, color: C.blue, fontWeight: 500 }}>
                            Add price tier
                        </Button>
                    </FormSection>

                    {/* ── 6. Technical Specifications ──────────────────────── */}
                    <FormSection title="Technical Specifications" badge={parameters.length ? `${parameters.length} params` : null}>
                        <Typography sx={{ fontSize: 12, color: C.muted, mb: 1.5 }}>
                            Add any technical parameters for this part (e.g. Capacitance = 100nF, Voltage = 16V, Tolerance = ±10%).
                        </Typography>
                        {parameters.length > 0 && (
                            <Grid container spacing={1} sx={{ mb: 1 }}>
                                {['Parameter Name', 'Value', 'Unit (optional)', ''].map((h, i) => (
                                    <Grid item xs={i === 3 ? 1 : i === 2 ? 2 : 4} key={i}>
                                        <Typography sx={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                            {h}
                                        </Typography>
                                    </Grid>
                                ))}
                            </Grid>
                        )}
                        {parameters.map((p, i) => (
                            <Grid container spacing={1} key={i} alignItems="center" sx={{ mb: 0.8 }}>
                                <Grid item xs={4}>
                                    <TextField fullWidth size="small" placeholder="e.g. Capacitance"
                                        value={p.parameter_name}
                                        onChange={(e) => {
                                            const rows = [...parameters];
                                            rows[i] = { ...rows[i], parameter_name: e.target.value };
                                            setParameters(rows);
                                        }}
                                        sx={inputSx} />
                                </Grid>
                                <Grid item xs={4}>
                                    <TextField fullWidth size="small" placeholder="e.g. 100nF"
                                        value={p.value}
                                        onChange={(e) => {
                                            const rows = [...parameters];
                                            rows[i] = { ...rows[i], value: e.target.value };
                                            setParameters(rows);
                                        }}
                                        sx={inputSx} />
                                </Grid>
                                <Grid item xs={2}>
                                    <TextField fullWidth size="small" placeholder="e.g. F, V, Ω"
                                        value={p.value_unit || ''}
                                        onChange={(e) => {
                                            const rows = [...parameters];
                                            rows[i] = { ...rows[i], value_unit: e.target.value };
                                            setParameters(rows);
                                        }}
                                        sx={inputSx} />
                                </Grid>
                                <Grid item xs={1}>
                                    <IconButton size="small" sx={{ color: '#c62828' }}
                                        onClick={() => setParameters(parameters.filter((_, j) => j !== i))}>
                                        <RemoveRowIcon sx={{ fontSize: 18 }} />
                                    </IconButton>
                                </Grid>
                            </Grid>
                        ))}
                        <Button size="small" startIcon={<AddRowIcon sx={{ fontSize: 16 }} />}
                            onClick={() => setParameters([...parameters, { parameter_name: '', value: '', value_unit: '' }])}
                            sx={{ textTransform: 'none', fontSize: 13, color: C.blue, fontWeight: 500 }}>
                            Add parameter
                        </Button>
                    </FormSection>

                    {/* ── 7. Lifecycle Status ───────────────────────────────── */}
                    <FormSection title="Lifecycle Status">
                        <Grid container spacing={2} alignItems="flex-start">
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth size="small">
                                    <InputLabel shrink>Status</InputLabel>
                                    <Select notched displayEmpty value={formData.lifecycle_status} label="Status"
                                        onChange={set('lifecycle_status')} sx={selectSx}>
                                        <MenuItem value="" sx={{ fontSize: 13 }}>— Unknown —</MenuItem>
                                        {[
                                            { value: 'active', label: 'Active' },
                                            { value: 'not_for_new_designs', label: 'Not For New Designs (NFND)' },
                                            { value: 'last_time_buy', label: 'Last Time Buy' },
                                            { value: 'obsolete', label: 'Obsolete' },
                                            { value: 'discontinued', label: 'Discontinued' },
                                        ].map(({ value, label }) => (
                                            <MenuItem key={value} value={value} sx={{ fontSize: 13 }}>{label}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField fullWidth label="Last Buy Date" value={formData.date_last_buy_chance}
                                    onChange={set('date_last_buy_chance')} size="small" sx={inputSx}
                                    type="date" slotProps={{ inputLabel: { shrink: true } }}
                                    helperText="For EOL / last-time-buy parts" />
                            </Grid>
                            <Grid item xs={12} sm={2} sx={{ display: 'flex', alignItems: 'center', pt: '10px !important' }}>
                                <FormControlLabel
                                    control={<Checkbox checked={formData.end_of_life} onChange={setCheck('end_of_life')} size="small" />}
                                    label={<Typography sx={{ fontSize: 13 }}>End of Life</Typography>}
                                />
                            </Grid>
                        </Grid>
                    </FormSection>

                    {/* ── 8. Compliance & Environmental ─────────────────────── */}
                    <FormSection title="Compliance & Environmental">
                        <Grid container spacing={2}>
                            {/* Row 1: compliance dropdowns */}
                            <Grid item xs={12} sm={4}>
                                <FormControl fullWidth size="small">
                                    <InputLabel shrink>RoHS</InputLabel>
                                    <Select notched displayEmpty value={formData.rohs_status} label="RoHS"
                                        onChange={set('rohs_status')} sx={selectSx}>
                                        <MenuItem value="" sx={{ fontSize: 13 }}>— Unknown —</MenuItem>
                                        {['RoHS3 Compliant', 'RoHS2 Compliant', 'RoHS Compliant', 'Non-RoHS', 'RoHS Exempt'].map(v => (
                                            <MenuItem key={v} value={v} sx={{ fontSize: 13 }}>{v}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <FormControl fullWidth size="small">
                                    <InputLabel shrink>REACH</InputLabel>
                                    <Select notched displayEmpty value={formData.reach_status} label="REACH"
                                        onChange={set('reach_status')} sx={selectSx}>
                                        <MenuItem value="" sx={{ fontSize: 13 }}>— Unknown —</MenuItem>
                                        {['REACH Unaffected', 'REACH Affected', 'REACH Compliant'].map(v => (
                                            <MenuItem key={v} value={v} sx={{ fontSize: 13 }}>{v}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <FormControl fullWidth size="small">
                                    <InputLabel shrink>MSL</InputLabel>
                                    <Select notched displayEmpty value={formData.moisture_sensitivity_level} label="MSL"
                                        onChange={set('moisture_sensitivity_level')} sx={selectSx}>
                                        <MenuItem value="" sx={{ fontSize: 13 }}>— Unknown —</MenuItem>
                                        {[
                                            'Level 1 - Unlimited',
                                            'Level 2 - 1 Year',
                                            'Level 2a - 4 Weeks',
                                            'Level 3 - 168 Hours',
                                            'Level 4 - 72 Hours',
                                            'Level 5 - 48 Hours',
                                            'Level 5a - 24 Hours',
                                            'Level 6 - TOL',
                                        ].map(v => (
                                            <MenuItem key={v} value={v} sx={{ fontSize: 13 }}>{v}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            {/* Row 2: trade/export fields */}
                            <Grid item xs={12} sm={4}>
                                <TextField fullWidth label="Export Control Class (ECCN)" value={formData.export_control_class}
                                    onChange={set('export_control_class')} size="small" sx={inputSx}
                                    placeholder="e.g. EAR99, 3A001" />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField fullWidth label="HTS Code" value={formData.htsus_code}
                                    onChange={set('htsus_code')} size="small" sx={inputSx}
                                    placeholder="e.g. 8542.31.0001" />
                            </Grid>
                            {/* Row 3: certification checkboxes */}
                            <Grid item xs={12}>
                                <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
                                    <FormControlLabel
                                        control={<Checkbox checked={formData.aec_qualified} onChange={setCheck('aec_qualified')} size="small" />}
                                        label={<Typography sx={{ fontSize: 13 }}>AEC-Q Qualified</Typography>}
                                    />
                                    <FormControlLabel
                                        control={<Checkbox checked={formData.halogen_free} onChange={setCheck('halogen_free')} size="small" />}
                                        label={<Typography sx={{ fontSize: 13 }}>Halogen Free</Typography>}
                                    />
                                    <FormControlLabel
                                        control={<Checkbox checked={formData.lead_free} onChange={setCheck('lead_free')} size="small" />}
                                        label={<Typography sx={{ fontSize: 13 }}>Lead Free</Typography>}
                                    />
                                </Stack>
                            </Grid>
                        </Grid>
                    </FormSection>

                    {/* ── 9. Media & Documentation ─────────────────────────── */}
                    <FormSection title="Media & Documentation">
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField fullWidth label="Datasheet URL" value={formData.datasheet_url}
                                    onChange={set('datasheet_url')} size="small" sx={inputSx}
                                    placeholder="https://..." />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField fullWidth label="Photo URL" value={formData.photo_url}
                                    onChange={set('photo_url')} size="small" sx={inputSx}
                                    placeholder="https://..." />
                            </Grid>
                        </Grid>

                        <Box sx={{ mt: 2 }}>
                            <Typography sx={{ fontSize: 13, fontWeight: 600, color: C.text, mb: 1 }}>
                                {editingComponent ? 'Product Image' : 'Upload Product Image'}
                            </Typography>
                            <Box
                                onClick={() => !imagePreview && fileInputRef.current?.click()}
                                sx={{
                                    p: 3, border: `2px dashed ${C.border}`, borderRadius: C.radius,
                                    textAlign: 'center', bgcolor: C.bg,
                                    cursor: imagePreview ? 'default' : 'pointer',
                                    '&:hover': { borderColor: C.blue },
                                    transition: 'border-color 0.15s',
                                }}>
                                {imagePreview ? (
                                    <>
                                        <Box component="img" src={imagePreview} alt="preview"
                                            sx={{ maxWidth: '100%', maxHeight: 140, objectFit: 'contain', mb: 1.5, borderRadius: C.radius }} />
                                        <br />
                                        <Button size="small" sx={{ color: C.blue, fontSize: 12, mr: 1 }}
                                            onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}>
                                            Replace
                                        </Button>
                                        <Button size="small" sx={{ color: '#c62828', fontSize: 12 }}
                                            onClick={(e) => { e.stopPropagation(); setImageFile(null); setImagePreview(''); }}>
                                            Remove
                                        </Button>
                                    </>
                                ) : (
                                    <>
                                        <UploadIcon sx={{ fontSize: 32, color: C.muted, mb: 0.5 }} />
                                        <Typography sx={{ fontSize: 13, color: C.muted, mb: 1 }}>
                                            Drop image here or click to upload
                                        </Typography>
                                    </>
                                )}
                                <input ref={fileInputRef} type="file" hidden
                                    accept="image/jpeg,image/png,image/webp,image/gif"
                                    onChange={handleFileChange} />
                            </Box>
                        </Box>
                    </FormSection>

                    {uploading && <LinearProgress sx={{ mt: 1 }} />}
                    {saveMutation.isError && (
                        <Alert severity="error" sx={{ mt: 1.5 }}>
                            {saveMutation.error?.message || 'Failed to save listing'}
                        </Alert>
                    )}
                </DialogContent>

                <Divider />
                <DialogActions sx={{ p: 2, gap: 1 }}>
                    <Button onClick={handleCloseDialog} sx={{
                        color: C.text, textTransform: 'none', fontWeight: 500,
                        border: `1px solid ${C.border}`, borderRadius: C.radius,
                    }}>
                        Cancel
                    </Button>
                    {!editingComponent && (
                        <Button variant="outlined"
                            disabled={saveMutation.isPending || uploading || !formData.price}
                            onClick={() => handleSubmit('draft')}
                            sx={{
                                borderColor: C.border, color: C.text, textTransform: 'none',
                                fontWeight: 500, borderRadius: C.radius,
                                '&:hover': { borderColor: C.blue, color: C.blue },
                            }}>
                            Save as draft
                        </Button>
                    )}
                    <Button variant="contained"
                        disabled={
                            saveMutation.isPending || uploading ||
                            !formData.mpn || !formData.price ||
                            !formData.listed_quantity || !formData.available_quantity
                        }
                        onClick={() => handleSubmit('active')}
                        sx={{
                            bgcolor: C.blue, borderRadius: C.radius, textTransform: 'none',
                            fontWeight: 600, boxShadow: 'none',
                            '&:hover': { bgcolor: C.blueDark, boxShadow: 'none' },
                            '&:disabled': { bgcolor: C.border, color: C.muted },
                        }}>
                        {saveMutation.isPending || uploading ? 'Saving…'
                            : editingComponent ? 'Update listing' : 'Publish listing'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

// ─── Tab button ───────────────────────────────────────────────────────────────
const ShopTab = ({ active, icon, label, onClick }) => (
    <Box onClick={onClick} sx={{
        display: 'flex', alignItems: 'center', gap: 1, px: 3, py: 1.8,
        cursor: 'pointer', fontSize: 14, fontWeight: active ? 600 : 400,
        color: active ? C.blue : C.muted,
        borderBottom: active ? `2px solid ${C.blue}` : '2px solid transparent',
        transition: 'all 0.15s', userSelect: 'none',
        '&:hover': { color: C.blue },
    }}>
        {React.cloneElement(icon, { sx: { fontSize: 18 } })}
        {label}
    </Box>
);

// ─── My Shop Page ─────────────────────────────────────────────────────────────
const MyShopPage = () => {
    const [tab, setTab] = useState(0);

    return (
        <Box sx={{ bgcolor: C.bg, minHeight: '100vh' }}>
            {/* Page header — full width */}
            <Box sx={{ bgcolor: C.white, borderBottom: `1px solid ${C.border}`, px: 4, py: 2.5 }}>
                <Stack direction="row" alignItems="center" spacing={2}>
                    <Box sx={{
                        width: 40, height: 40, borderRadius: C.radius,
                        bgcolor: C.blue, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        <StorefrontIcon sx={{ fontSize: 20, color: '#fff' }} />
                    </Box>
                    <Box>
                        <Typography sx={{ fontSize: 18, fontWeight: 700, color: C.text, lineHeight: 1.2 }}>
                            My Shop
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: C.muted }}>
                            Manage your listings and orders
                        </Typography>
                    </Box>
                </Stack>
            </Box>

            {/* Content — full width */}
            <Box sx={{ bgcolor: C.white, borderBottom: `1px solid ${C.border}` }}>
                {/* Tabs */}
                <Stack direction="row" sx={{ px: 2, borderBottom: `1px solid ${C.border}` }}>
                    <ShopTab active={tab === 0} icon={<InventoryIcon />} label="Listings" onClick={() => setTab(0)} />
                    <ShopTab active={tab === 1} icon={<OrdersIcon />}    label="Orders"   onClick={() => setTab(1)} />
                </Stack>

                {/* Tab content — no inner padding, full width */}
                {tab === 0 && <ListingsTab />}
                {tab === 1 && <SoldOrdersTab />}
            </Box>
        </Box>
    );
};

export default MyShopPage;